import { inject, Injectable } from '@angular/core';
import {
  FollowUp,
  FollowUpLinkType,
  FollowUpsUser,
} from '@app/modules/follow-ups/follow-ups.types';
import { FollowUpsUtilsService } from '@app/modules/follow-ups/services/follow-ups-utils.service';
import { FollowUpsService } from '@app/modules/follow-ups/services/follow-ups.service';
import {
  ArchiveFollowUpsAction,
  AssignFollowUpsAction,
  CreateFollowUpsAction,
  DeleteFollowUpAction,
  GetFollowUpAction,
  GetFollowUpsListAction,
  GetFollowUpsUsersListAction,
  ResolveFollowUpsAction,
  SetFollowUpNotesAction,
} from '@app/modules/follow-ups/store/follow-ups.actions';
import { GetLaboratoryBatchListAction } from '@app/modules/laboratory/store/laboratory.actions';
import { GetSamplesListAction } from '@app/modules/service-data/store/sample.actions';
import { GetServiceDataListAction } from '@app/modules/service-data/store/service-data.actions';
import {
  ApiFilters,
  Pagination,
  SortDirection,
  StatePageSettings,
} from '@core/core.types';
import { LoadKitsByIdAction } from '@core/store/kits/kits.actions';
import { Util } from '@core/utils/core.util';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { isEqual } from 'lodash';
import { combineLatest, EMPTY, forkJoin, of, switchMap } from 'rxjs';
import { catchError, delay, first, tap } from 'rxjs/operators';
import { GetReportsListAction } from '@app/modules/reports/store/reports.actions';
import { GetResultsListAction } from '@app/modules/reports/store/results.actions';

const FOLLOW_UPS_STATE_NAME = 'followUps';

export interface FollowUpsStateModel {
  followUps: { [id: string]: FollowUp };
  followUpsUsers: FollowUpsUser[];
  pages?: {
    [key: string]: {
      items: string[];
      continuationToken: string;
    };
  };
  currentPageSettings: StatePageSettings<FollowUpsFilters>;
  totalCount?: number;
}

@State<FollowUpsStateModel>({
  name: FOLLOW_UPS_STATE_NAME,
  defaults: {
    followUps: {},
    followUpsUsers: [],
    pages: {},
    currentPageSettings: {
      pageIndex: 0,
      sortSettings: {
        orderBy: 'Generated',
        sortDirection: SortDirection.desc,
        pageSize: 30,
      },
    },
  },
})
@Injectable()
export class FollowUpsState {
  private followUpsService = inject(FollowUpsService);
  private followUpsUtilsService = inject(FollowUpsUtilsService);

  @Selector()
  static getFollowUpById(state: FollowUpsStateModel) {
    return (followUpId: string) => state.followUps[followUpId];
  }

  @Selector()
  static getFollowUpsByIds(state: FollowUpsStateModel) {
    return (followUpIds: string[]) =>
      followUpIds
        ?.map((followUpId) => state.followUps[followUpId])
        .filter((followUp) => !!followUp);
  }

  @Selector()
  static getFollowUpsUsers(state: FollowUpsStateModel) {
    return state.followUpsUsers;
  }

  @Selector()
  static getCurrentPageIds(state: FollowUpsStateModel) {
    const {
      currentPageSettings: { pageIndex },
      pages,
    } = state;

    if (
      Object.keys(pages).length === 0 ||
      pageIndex > Object.keys(pages).length ||
      pageIndex < 0
    ) {
      return [];
    }
    return state.pages[pageIndex].items.slice();
  }

  @Selector()
  static getPagination({
    currentPageSettings,
    totalCount,
    pages,
  }: FollowUpsStateModel): Pagination<FollowUpsFilters> {
    return {
      currentPageSettings: {
        ...currentPageSettings,
      },
      totalCount,
      pagesSize: Object.keys(pages).length,
      collectionSize: currentPageSettings.sortSettings.pageSize,
    };
  }

  @Action(GetFollowUpsUsersListAction)
  getFollowUpsUsersListAction({
    patchState,
  }: StateContext<FollowUpsStateModel>) {
    this.followUpsService.getUsers().subscribe((users) => {
      patchState({
        followUpsUsers: users,
      });
    });
  }

  @Action(GetFollowUpAction)
  getFollowUpAction(
    { getState, patchState }: StateContext<FollowUpsStateModel>,
    { followUpId }: GetFollowUpAction
  ) {
    return this.followUpsService.getFollowUp(followUpId).pipe(
      tap((followUp) => {
        patchState({
          followUps: {
            ...getState().followUps,
            [followUp.id]: followUp,
          },
        });
      })
    );
  }

  @Action(CreateFollowUpsAction)
  createFollowUpsAction(
    { dispatch }: StateContext<FollowUpsStateModel>,
    { ids, linkType, type, reason }: CreateFollowUpsAction
  ) {
    return combineLatest(
      ids.map((id) =>
        this.followUpsService.createFollowUp({
          link: { id: id, type: linkType },
          reason,
          type,
        })
      )
    ).pipe(
      delay(500),
      first(),
      switchMap((data) =>
        forkJoin([
          dispatch(
            new GetFollowUpsListAction(
              { ids: data.map((d) => d.id) },
              {},
              null,
              null
            )
          ),
          dispatch(
            (() => {
              switch (linkType) {
                case FollowUpLinkType.Sample:
                  return new GetSamplesListAction({ ids }, {}, null, true);
                case FollowUpLinkType.Kit:
                  return new LoadKitsByIdAction(ids, true);
                case FollowUpLinkType.ServiceData:
                  return new GetServiceDataListAction({ ids }, {}, null, true);
                case FollowUpLinkType.Report:
                  return new GetReportsListAction({ ids }, {}, null, true);
                case FollowUpLinkType.Result:
                  return new GetResultsListAction({ ids }, {}, null, true);
                default:
                  return EMPTY;
              }
            })()
          ),
        ])
      )
    );
  }

  @Action(ArchiveFollowUpsAction)
  archiveFollowUpsAction(
    { dispatch }: StateContext<FollowUpsStateModel>,
    { followUpIds }: ArchiveFollowUpsAction
  ) {
    return this.followUpsService.archiveFollowUps(followUpIds).pipe(
      delay(300),
      switchMap(() =>
        dispatch(
          new GetFollowUpsListAction({ ids: followUpIds }, {}, null, true)
        )
      )
    );
  }

  @Action(DeleteFollowUpAction)
  deleteFollowUpAction(
    { getState, patchState }: StateContext<FollowUpsStateModel>,
    { followUpId }: DeleteFollowUpAction
  ) {
    return this.followUpsService.deleteFollowUp(followUpId).pipe(
      tap(() =>
        patchState({
          followUps: { ...getState().followUps, [followUpId]: null },
        })
      )
    );
  }

  @Action(ResolveFollowUpsAction)
  resolveFollowUpsAction(
    { dispatch }: StateContext<FollowUpsStateModel>,
    { followUpIds }: ResolveFollowUpsAction
  ) {
    return this.followUpsService.resolveFollowUps(followUpIds).pipe(
      delay(300),
      switchMap(() =>
        dispatch(
          new GetFollowUpsListAction({ ids: followUpIds }, {}, null, true)
        )
      )
    );
  }

  @Action(AssignFollowUpsAction)
  assignFollowUpsAction(
    { dispatch }: StateContext<FollowUpsStateModel>,
    { followUpIds, assigneeId }: AssignFollowUpsAction
  ) {
    return this.followUpsService.assignFollowUps(followUpIds, assigneeId).pipe(
      delay(300),
      switchMap(() =>
        dispatch(
          new GetFollowUpsListAction({ ids: followUpIds }, {}, null, true)
        )
      )
    );
  }

  @Action(SetFollowUpNotesAction)
  setNotesAction(
    { dispatch }: StateContext<FollowUpsStateModel>,
    { followUpId, notes }: SetFollowUpNotesAction
  ) {
    return this.followUpsService.setNotes(followUpId, notes).pipe(
      delay(300),
      switchMap(() => dispatch(new GetFollowUpAction(followUpId)))
    );
  }

  @Action(GetFollowUpsListAction)
  getFollowUpsListAction(
    { getState, patchState, dispatch }: StateContext<FollowUpsStateModel>,
    {
      filters,
      headers,
      pageToLoad = 1,
      reload = false,
    }: GetLaboratoryBatchListAction
  ) {
    // Appends objects are added, in case of separate state @Action calls, as they would otherwise override each other!
    const followUpsAppends = {};
    const followUpsList = { ...getState().followUps };

    const state = getState();
    let pages = { ...state.pages };
    const currentPageSettings = {
      ...state.currentPageSettings,
      sortSettings: { ...state.currentPageSettings.sortSettings },
      filterSettings: { ...state.currentPageSettings.filterSettings },
    };
    let totalCount = state.totalCount;

    let actualPageToLoad = pageToLoad ? pageToLoad - 1 : undefined;

    // Update pagination in case of listing pages.
    if (actualPageToLoad !== undefined) {
      currentPageSettings.pageIndex = actualPageToLoad;

      // Check that filters are same as they were for previous page.
      if (
        !isEqual(currentPageSettings.filterSettings, filters) ||
        currentPageSettings.pageSize !== headers.pagingTop ||
        currentPageSettings.sortSettings.sortDirection !==
          headers.orderDirection ||
        currentPageSettings.sortSettings.orderBy !== headers.orderBy
      ) {
        // If filters are different as they were -> update filters, reset pages object and go back to page 1.
        currentPageSettings.pageSize = headers.pagingTop;
        currentPageSettings.sortSettings.sortDirection = headers.orderDirection;
        currentPageSettings.sortSettings.orderBy = headers.orderBy;
        currentPageSettings.sortSettings.pageSize = headers.pagingTop;
        currentPageSettings.filterSettings = filters;

        actualPageToLoad = 0;
        reload = true;
        pages = {};
        currentPageSettings.pageIndex = actualPageToLoad;
      }
    }

    // If reload is not required, check if we already hold service data with selected properties.
    if (filters.ids && !reload) {
      filters.ids = filters.ids.filter((item) => !followUpsList[item]);

      // If we filter out all IDs, return early!
      if (!filters.ids.length) {
        return EMPTY;
      }
    }

    if (filters.ids?.length > 100) {
      return forkJoin(
        Util.chunkArray(filters.ids, 100).map((ids) =>
          dispatch(new GetFollowUpsListAction({ ids }, headers, null, reload))
        )
      );
    }

    // Get selected page continuation token, if not first page
    if (actualPageToLoad > 0) {
      headers.continuationToken =
        pages[actualPageToLoad - 1]?.continuationToken;
    }

    // If not listing page, page doesnt exist or reload is demanded -> call API
    if (actualPageToLoad === undefined || !pages[actualPageToLoad] || reload) {
      return this.followUpsService
        .listFollowUps(
          this.followUpsUtilsService.buildHttpParams(filters),
          headers
        )
        .pipe(
          tap((response) => {
            response.items.forEach((followUp) => {
              followUpsAppends[followUp.id] = followUp;
            });

            // Add/update pages
            if (actualPageToLoad !== undefined) {
              totalCount = response.totalCount;
              pages[actualPageToLoad] = {
                items: response.items.map((followUp) => followUp.id),
                continuationToken: response.continuationToken,
              };
            }
          }),
          tap(() => {
            // get latest state again as state might be patched in some other @Action
            const oldState = getState();
            patchState({
              followUps: { ...oldState.followUps, ...followUpsAppends },
              pages,
              totalCount,
              currentPageSettings,
            });
          }),
          catchError((err) => {
            patchState({});
            return of(null);
          })
        );
    } else {
      // If page exists -> update state so pagination is updated.
      patchState({ currentPageSettings });
      return EMPTY;
    }
  }
}

export interface FollowUpsFilters extends ApiFilters {
  ids?: string[];
  referenceKey?: string;
  type?: string;
  reasonToken?: string;
  notesToken?: string;
  assignees?: string[];
  linkId?: string;
  linkType?: string;
  reporter?: string;
  lastStatuses?: string[];
  lastStatus?: string;
  lastStatusChangedFrom?: string;
  lastStatusChangedTo?: string;
  status?: string;
  statusChangedFrom?: string;
  statusChangedTo?: string;
}
